import React from "react";
import { LineChart, Line, Tooltip, YAxis } from "recharts";

export default function GrowChart({ aProfile, isMobile }) {
    //console.log('.followersDynamic', aProfile.followersDynamic);
    // Parse the followers data or create empty array if not available
    let followersData = [];
    try {
        if (aProfile.followersDynamic) {
            followersData = typeof aProfile.followersDynamic === 'string'
                ? JSON.parse(aProfile.followersDynamic)
                : aProfile.followersDynamic;
        }
    } catch (e) {
        // Handle parsing error
    }

    // Get the current date and find the most recent Monday (start of week)
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 is Sunday, 1 is Monday, etc.
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Calculate days since Monday
    
    const mondayDate = new Date(today);
    mondayDate.setDate(today.getDate() - daysSinceMonday);
    mondayDate.setHours(0, 0, 0, 0); // Set to beginning of the day
        
    // Filter data to only include entries from this week (Monday to today)
    let weekData = followersData.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= mondayDate && itemDate <= today;
    });

    // If we don't have any data for this week, create some default data
    if (weekData.length === 0) {
        const startFollowers = followersData.length > 0 ? 
            followersData[followersData.length - 1].followers : 
            100;
            
        // Create data for each day from Monday to today
        for (let i = 0; i <= daysSinceMonday; i++) {
            const date = new Date(mondayDate);
            date.setDate(mondayDate.getDate() + i);
            weekData.push({
                date: date.toISOString().split('T')[0],
                followers: startFollowers + (i * 3) // Modest organic growth
            });
        }
    }

    // Create the full week data including projected days
    const daysInWeek = 7;
    const daysLeftInWeek = daysInWeek - daysSinceMonday - 1;
    
    // Get the latest follower count
    const latestFollowers = weekData.length > 0 ? 
        weekData[weekData.length - 1].followers : 
        100;
    
    // Add projected data for the remaining days of the week
    const fullWeekData = [...weekData];
    
    for (let i = 1; i <= daysLeftInWeek; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        fullWeekData.push({
            date: date.toISOString().split('T')[0],
            projectedFollowers: latestFollowers + (i * 15) // Higher projected growth
        });
    }

    // Add projectedFollowers to all days in the data
    const combinedData = fullWeekData.map((item, index) => {
        const growthPerDay = 15; // Define the growth per day
        let projectedFollowers;

        if (item.followers) {
            // If we have actual follower data for this day, use it as the base
            projectedFollowers = item.followers + (index * growthPerDay);
        } else {
            // If it's a projected day, calculate based on the latest follower count
            projectedFollowers = latestFollowers + (index * growthPerDay);
        }

        return {
            ...item,
            projectedFollowers: projectedFollowers
        };
    });

    //console.log('combinedData', combinedData);

    // Calculate the min and max values for proper scaling
    const allValues = combinedData.flatMap(item => [
        item.followers || 0, 
        item.projectedFollowers || 0
    ]).filter(val => val > 0); // Filter out zero values
    
    const minValue = Math.floor(Math.min(...allValues));
    const maxValue = Math.ceil(Math.max(...allValues));
    
    // Calculate the range of the actual data
    const dataRange = maxValue - minValue;
    
    // Introduce a scaling factor to exaggerate the difference
    const scaleFactor = 2; // Adjust this value to control the gap
    const scaledMinValue = minValue;
    const scaledMaxValue = minValue + (dataRange * scaleFactor);

    // Create a focused domain that zooms in on the scaled data range
    const percentPadding = 0.1; // 10% padding
    const focusedMin = Math.max(0, scaledMinValue - Math.ceil((scaledMaxValue - scaledMinValue) * 0.05)); // Small bottom padding
    const focusedMax = scaledMaxValue + Math.ceil((scaledMaxValue - scaledMinValue) * percentPadding); // Top padding

    // For very small ranges, ensure we have at least some minimum difference
    const minDomainRange = 50; // Minimum range to display
    const adjustedMin = (focusedMax - focusedMin < minDomainRange) ? 
        focusedMax - minDomainRange : focusedMin;
    
    // Final domain values (ensuring we don't go negative)
    const domainMin = Math.max(0, adjustedMin);
    const domainMax = focusedMax;
/*
    console.log('domainMin', domainMin);
    console.log('domainMax', domainMax);
    console.log('minValue', minValue);
    console.log('maxValue', maxValue);
    console.log('dataRange', dataRange);
    console.log('focusedScale', true); // Log that we're using the focused scale
    console.log('scaleFactor', scaleFactor); // Log the scale factor
*/
    // Format dates for display
    const formatDate = (dateStr) => {
        //date string is like 5 for Friday etc so return Fri for 5 etc
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        return days[Number(dateStr)+1];
    };

    // Custom tooltip formatter to display actual follower data
    const tooltipFormatter = (value, name, entry) => {
        //console.log('tooltipFormatter', value, name, entry);
        if (name === 'Potential') {
            return [entry.payload.projectedFollowers, 'Potential Followers'];
        } else {
            return [entry.payload.followers, 'Actual Followers'];
        }
    };

    return (
        <div style={{ 
            width: '100%', 
            height: '130px', // Made more compact
            overflowX: 'auto',
            display: 'flex',
            justifyContent: 'center'
        }}>
            <LineChart
                width={isMobile ? window.innerWidth - 40 : 600}
                height={100}
                data={combinedData}
                margin={{ top: 10, right: 10, left: 10, bottom: 10 }} // Reduced margins
                // Use explicit min/max domain values to zoom in on the data
                domain={[domainMin, domainMax]}
            >
                <YAxis domain={[domainMin, domainMax]} hide={true} />
                <Tooltip
                    formatter={tooltipFormatter}
                    labelFormatter={(label) => formatDate(label)}
                />
                <Line
                    type="monotone"
                    dataKey="followers"
                    name="Actual"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    // Use the zoomed-in domain
                    domain={[domainMin, domainMax]}
                />
                <Line
                    type="monotone"
                    dataKey="projectedFollowers"
                    name="Potential"
                    stroke="#ff0000"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={{ r: 3 }}
                    // Use the zoomed-in domain
                    domain={[domainMin, domainMax]}
                />
            </LineChart>
        </div>
    );
}